import { BodyText } from '@/shared/components/StyledText';
import { green, mint, white } from '@/shared/styles/colors';
import styled, { css } from '@emotion/native';
import { Entypo } from '@expo/vector-icons';
import React from 'react';
import { ScrollView, TextStyle, ViewStyle } from 'react-native';
import { Menu, TouchableRipple } from 'react-native-paper';
import { InputLabel } from './StaticInput';
import { toTitleCase } from '@/shared/utils';

interface Option {
  label: string;
  value: string | number;
}

interface DropdownProps {
  options: Option[];
  selectedOption: string;
  selectedOptionLabel?: string;
  onSelect: (option: Option) => void;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  eyebrowText?: string;
  eyebrowTextStyle?: TextStyle;
  customMenuContentStyle?: ViewStyle;
  customDropdownButtonStyle?: ViewStyle;
  dropdownTextStyle?: TextStyle;
  selectedOptionContentStyle?: ViewStyle;
  selectedOptionTextStyle?: TextStyle;
  useTitleCase?: boolean;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedOption,
  selectedOptionLabel,
  isVisible,
  setIsVisible,
  onSelect,
  eyebrowText,
  eyebrowTextStyle,
  customMenuContentStyle,
  customDropdownButtonStyle,
  dropdownTextStyle,
  selectedOptionContentStyle,
  selectedOptionTextStyle,
  useTitleCase = false,
  disabled = false,
}) => {
  const displayText = selectedOptionLabel || selectedOption;
  const formattedDisplayText = useTitleCase
    ? toTitleCase(displayText)
    : displayText;

  return (
    <Menu
      visible={isVisible}
      onDismiss={() => setIsVisible(false)}
      anchor={
        <DropdownButton
          onPress={() => !disabled && setIsVisible(true)}
          style={customDropdownButtonStyle}
          disabled={disabled}
        >
          <>
            <InputLabel
              isVisible={Boolean(eyebrowText)}
              style={eyebrowTextStyle}
            >
              {eyebrowText}
            </InputLabel>
            <DropdownContent>
              <DropdownContentText style={dropdownTextStyle}>
                {formattedDisplayText}
              </DropdownContentText>
              <Entypo
                name="chevron-thin-down"
                size={16}
                color={green}
                style={
                  isVisible
                    ? {
                        transform: [{ rotate: '180deg' }],
                      }
                    : {}
                }
              />
            </DropdownContent>
          </>
        </DropdownButton>
      }
      contentStyle={{ ...menuContentStyle, ...customMenuContentStyle }}
    >
      <ScrollView>
        {options.map((option) => {
          const isSelected = selectedOption === option.value.toString();
          return (
            <Menu.Item
              key={option.value}
              title={option.label}
              onPress={() => {
                onSelect(option);
                setIsVisible(false);
              }}
              style={isSelected ? selectedOptionContentStyle : undefined}
              titleStyle={[
                menuItemStyle,
                dropdownTextStyle,
                isSelected && selectedOptionTextStyle,
              ]}
            />
          );
        })}
      </ScrollView>
    </Menu>
  );
};

// Same size, corners and border as StaticInput
const DropdownButton = styled(TouchableRipple)({
  backgroundColor: white,
  borderRadius: 36,
  paddingLeft: 26,
  marginBottom: 28,
  height: 50,
  justifyContent: 'center',
  borderColor: mint,
  borderWidth: 1,
  width: 326,
});

const DropdownContent = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
  paddingRight: 16,
});

const DropdownContentText = styled(BodyText)({
  color: green,
});

const menuContentStyle = css({
  backgroundColor: white,
  width: 326,
  top: 48,
  borderRadius: 10,
  maxHeight: 260,
});

const menuItemStyle = css({
  color: green,
});

export default Dropdown;
